'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

const faqs = [
  { q: 'Do you offer subscription plans?', a: 'No. Our pricing is completely commission-based. You only pay when business happens.' },
  { q: 'Can I request custom features?', a: 'Yes. Contact us for fully customizable business solutions.' },
{
  q: 'Is there a setup fee?',
  a: 'Yes. An affordable one-time setup fee helps us configure and customize the platform for your business. After setup, pricing is based on successful transactions.'
},
  { q: 'How does the commission model work?', a: 'We earn a small commission on each successful transaction processed through the platform. No business, no charge.' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs?.map((faq) => ({
    '@type': 'Question',
    name: faq?.q,
    acceptedAnswer: { '@type': 'Answer', text: faq?.a },
  })),
};

export default function PricingContent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <section className="pt-24" aria-label="Pricing">
        {/* Hero */}
        <PageHero className="py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
                Transparent Pricing
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
                Simple, Transparent{' '}
                <span className="italic font-light text-white/80">Pricing.</span>
              </h1>
              <p className="text-white/70 text-xl max-w-2xl mx-auto leading-relaxed animate-on-scroll stagger-2">
                No subscriptions. No hidden fees. Pay only when you earn.
              </p>
            </div>
          </div>
        </PageHero>

        {/* Pricing Cards */}
        <div className="bg-[#FFFFFF] py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 items-stretch">

              {/* Card 1 — Commission-Based (Highlighted) */}
              <div className="animate-on-scroll stagger-1 relative flex flex-col rounded-2xl border border-[#0066FF] bg-[#0046AF] shadow-2xl shadow-[#0066FF]/20 transition-all duration-300 hover:shadow-[#0066FF]/30 hover:scale-[1.01]">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#0066FF] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    Our Model
                  </span>
                </div>

                <div className="p-8 flex-1">
                  <div className="mb-6">
                    <h2 className="font-display text-2xl font-semibold text-white mb-1">
                      Commission-Based
                    </h2>
                    <p className="text-sm font-medium text-[#BFDBFE]">
                      Pay only when business happens
                    </p>
                  </div>

                  <p className="text-white/80 text-sm leading-relaxed mb-8">
                    We grow when you grow. Our pricing is based on successful transactions, meaning you only pay when business happens.
                  </p>

                  <ul className="space-y-3" role="list" aria-label="Commission-based plan features">
                    {[
                      'No monthly subscription',
                      'One-time setup fee',
                      'Transparent commission model',
                      'Pay only on successful transactions',
                      'Scales with your business growth',
                      'Dedicated support included',
                    ]?.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5 text-[#93C5FD]" />
                        <span className="text-sm text-white/90">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-8 pt-0">
                  <Link
                    href="/demo-booking"
                    className="btn-primary w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold transition-all"
                    aria-label="Get Started with Commission-Based pricing"
                  >
                    Get Started
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>
              </div>

              {/* Card 2 — Custom Solutions */}
              <div className="animate-on-scroll stagger-2 relative flex flex-col rounded-2xl border border-[rgba(15,23,42,0.08)] bg-white shadow-sm transition-all duration-300 hover:shadow-lg hover:border-[rgba(0, 102, 255,0.3)]">
                <div className="p-8 flex-1">
                  <div className="mb-6">
                    <h2 className="font-display text-2xl font-semibold text-[#0F172A] mb-1">
                      Custom Solutions
                    </h2>
                    <p className="text-sm font-medium text-[#0066FF]">
                      Need something tailored?
                    </p>
                  </div>

                  <p className="text-[#475569] text-sm leading-relaxed mb-8">
                    Need something specific? We provide fully customizable solutions for businesses with unique requirements.
                  </p>

                  <ul className="space-y-3" role="list" aria-label="Custom solutions features">
                    {[
                      'Custom integrations',
                      'Enterprise requirements',
                      'Advanced workflows',
                      'Dedicated onboarding',
                      'Feature customization',
                    ]?.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5 text-[#0066FF]" />
                        <span className="text-sm text-[#475569]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-8 pt-0">
                  <Link
                    href="/contact"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold border border-[rgba(0, 102, 255,0.3)] text-[#0066FF] hover:bg-[rgba(0, 102, 255,0.06)] transition-all"
                    aria-label="Contact Us for Custom Solutions"
                  >
                    Contact Us
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Bottom Note */}
            <p className="text-center text-[#64748B] text-sm mt-10 animate-on-scroll stagger-3">
              Questions?{' '}
              <Link href="/contact" className="text-[#0066FF] hover:underline font-medium">
                Contact our team
              </Link>{' '}
              for a customized solution.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
<section
  className="py-24 relative overflow-hidden"
  aria-label="Pricing FAQ"
  style={{
    background:
      "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 50%, #EEF6FF 100%)",
  }}
>      
{/* Top Left */}
<div
  className="absolute -top-52 -left-52 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
  style={{
    background: "radial-gradient(circle, #60A5FA 0%, transparent 70%)",
  }}
/>

{/* Top Right */}
<div
  className="absolute -top-52 -right-52 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
  style={{
    background: "radial-gradient(circle, #60A5FA 0%, transparent 70%)",
  }}
/>

{/* Center Left */}
<div
  className="absolute top-1/2 -left-56 -translate-y-1/2 w-[550px] h-[550px] rounded-full blur-3xl opacity-18"
  style={{
    background: "radial-gradient(circle, #93C5FD 0%, transparent 70%)",
  }}
/>

{/* Center Right */}
<div
  className="absolute top-1/2 -right-56 -translate-y-1/2 w-[550px] h-[550px] rounded-full blur-3xl opacity-18"
  style={{
    background: "radial-gradient(circle, #93C5FD 0%, transparent 70%)",
  }}
/>

{/* Bottom Left */}
<div
  className="absolute -bottom-52 -left-52 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
  style={{
    background: "radial-gradient(circle, #60A5FA 0%, transparent 70%)",
  }}
/>

{/* Bottom Right */}
<div
  className="absolute -bottom-52 -right-52 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
  style={{
    background: "radial-gradient(circle, #60A5FA 0%, transparent 70%)",
  }}
/>
  <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll stagger-1">
              <h2 className="font-display text-4xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-[#475569] text-lg">Everything you need to know about our pricing.</p>
            </div>

            <div className="space-y-3" role="list">
              {faqs?.map((faq, i) => (
                <div
                  key={i}
                  className={`
animate-on-scroll
stagger-${i + 2}
rounded-2xl
bg-white/90
backdrop-blur-xl
border
border-[#DCE8F5]
shadow-lg
hover:shadow-2xl
hover:-translate-y-1
transition-all
duration-300
overflow-hidden
`}
                  role="listitem"
                >
                  <button
                    className="w-full flex items-center justify-between p-7 text-left hover:bg-[#F8FBFF] transition-all duration-300"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    aria-controls={`faq-answer-${i}`}
                  >
                    <span className="font-semibold text-[#0F172A] pr-4">{faq?.q}</span>
                 <div
  className={`transition-transform duration-300 ${
    openFaq === i ? 'rotate-180' : ''
  }`}
>
  <Icon
    name="ChevronDownIcon"
    size={18}
    className="text-[#0066FF]"
  />
</div>
                  </button>
                  {openFaq === i && (
                    <div
  id={`faq-answer-${i}`}
  className="px-7 pb-7 border-t border-[#E2E8F0] bg-[#FCFDFF]"
>
                      <p className="text-[#475569] leading-relaxed">{faq?.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
<div className="flex justify-center mt-20">
  <div className="w-40 h-px bg-gradient-to-r from-transparent via-[#0066FF] to-transparent" />
</div>
        {/* CTA */}
        <section className="py-20 bg-[#FFFFFF]" aria-label="Get started CTA">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div
              className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 shadow-2xl border border-[#E2E8F0] relative overflow-hidden"
style={{
  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 45%, #EAF4FF 100%)'
}}            >
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Ready to get started?
              </h2>
              <p className="text-[#475569] text-lg mb-8 max-w-xl mx-auto">
                Join operators who pay only when business happens. No risk, no upfront cost.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={18} />
                </Link>
                <Link href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-medium text-[#0F172A] border border-[#CBD5E1] hover:bg-white hover:border-[#94A3B8] transition-all duration-300">
                  <Icon name="ChatBubbleLeftRightIcon" size={16} />
                  Talk to Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </section>
    </>
  );
}